/**
 * Version Configuration
 * Automatically synced with package.json version
 */

import packageJson from '../../package.json'

// Parse version from package.json
const [major, minor, patch] = packageJson.version.split('.').map(Number)

export const VERSION = {
  // Semantic versioning: MAJOR.MINOR.PATCH (auto-synced from package.json)
  major,
  minor,
  patch,

  // Get formatted version string
  get full() {
    return `v${this.major}.${this.minor}.${this.patch}`
  },

  // Release name (optional - update manually for each release)
  name: 'GSC Join + Evidence-Based Scoring',

  // Release date
  date: '2026-08-12',

  // Build environment
  environment: process.env.NODE_ENV || 'development',
} as const

/**
 * The user-visible build stamp.
 *
 * Prefers the deploy's commit SHA, inlined at build time by `next.config.ts`
 * (see NEXT_PUBLIC_BUILD_SHA there), and falls back to the package version
 * locally and anywhere the SHA is absent.
 *
 * The semver alone was the stamp until 2026-09-21, and it is hand-edited with
 * no bump script, no CI step and no hook — so it read v1.29.0 for 40 days and
 * roughly 90 merged PRs while the site visibly changed. The SHA cannot drift
 * because nobody has to remember it.
 */
export function getVersion(): string {
  const sha = process.env.NEXT_PUBLIC_BUILD_SHA
  return sha ? `${VERSION.full}+${sha}` : VERSION.full
}

// Get version with release name
export function getVersionWithName(): string {
  return `${VERSION.full}${VERSION.name ? ` (${VERSION.name})` : ''}`
}

// Get full version info
export function getVersionInfo() {
  return {
    version: VERSION.full,
    name: VERSION.name,
    date: VERSION.date,
    environment: VERSION.environment,
  }
}

/**
 * Version History
 *
 * v1.29.0 (GSC Join + Evidence-Based Scoring) - 2026-08-12
 * Search Console join layer (#147-159) — Radar's first second-party data
 * source, joined against the audit rather than reported beside it:
 * - gsc_query_pages store + query-page join layer, sync watermark derived
 *   from stored rows, connection matched by normalized domain
 *   (migrations 143-145)
 * - GSC-only join checks; cannibalization clustered by page set, not by raw
 *   URL; visibility-to-click finding + page-sync pagination
 * - Every finding classified deterministic-or-judgement (T5), and ONE
 *   canonical action priority order (T4) shared by every consumer
 * - Keyset pagination + Postgres-side aggregation for the query feed
 * - /admin internal search reality dashboard
 *
 * Evidence-based scoring (#161-197) — the sweep that moved Radar from
 * "score anyway" to "withhold unless the evidence is there":
 * - Incomplete Answer Engine, Citation Check and Source Influence runs now
 *   withhold instead of scoring; Prompt SOV metric integrity; hallucination
 *   scoring made evidence-based; brand disambiguation scored per provider
 * - Schema Audit: representative page sampling, entity scoring and the
 *   scoring methodology itself corrected; sampling validated
 * - Entity + brand-mention measurement corrected; verified subject selection
 *   fixed; ground-truth extraction stabilized and its diagnostics persisted
 * - Perplexity citations deduplicated; Reddit evidence stabilized; Reddit
 *   Monitor captures sanitized Serper error detail
 * - Entity-resolution provenance recorded (migrations 141-142); citation
 *   entity confirmed before checkout
 * - GSC enrichment keyed to the audited domain; machine queries kept out of
 *   join findings
 *
 * Dashboard (this release):
 * - DashboardHero: three columns — score, signal profile, next best move —
 *   forked from ScoreHero so the marketing surfaces that import it
 *   (RadarDemo, HomeHero) are untouched
 * - SignalProfile radar polygon + SignalDetailBand; the five category cards
 *   retired from the dashboard (they repeated the chart) with the category
 *   filter control moving to the band
 * - MetricBand: full-width 4-up audit summary, every detail line derived
 * - TrendSparkline gained a viewWidth so a full-width band is not a
 *   330px-tall chart; nav icons to lucide LayoutGrid / SearchCheck at 1.8
 *
 * Marketing (#201-205, #210, #213-214):
 * - Homepage hero v2 baked in and the flag archived; tool-entry-v2 rolled
 *   out across 8 tool pages and archived
 * - /vector, /hive and /projects/vector rebuilt on their design references;
 *   Pixelmojo brand-system case study added
 * - Arabic Radar launched; contact strip, mobile card stacking, hero visual
 * - Web manifest + icon set
 *
 * Analytics + attribution (#177, #207-208, #210):
 * - CTA click tracking with an admin dashboard, gated on a GA4 dimension
 *   check; campaigns dashboard; GA4 reach panel on LLM referrals
 * - LLM referral attribution (migrations 138-139)
 * - Clarity window collisions handled; GA4-specific OAuth client pair
 *
 * Security + infra:
 * - External fetch hardened against SSRF (#144); anon/authenticated grants
 *   revoked (migration 140); teaser payload leak test
 * - undici, nanoid, brace-expansion, fast-uri, postcss, dompurify advisories;
 *   node pinned to 24
 * - IndexNow submission restored to the build; stale blog page caching fixed
 * - Tools JSON-LD guarded against schema drift
 *
 * Content:
 * - GEO agency pillar; entity-resolution series part 6; Radar tool-count
 *   accuracy corrected across posts
 *
 * v1.28.0 (Radar Dashboard Overhaul) - 2026-07-26
 * /platform/app UI overhaul (rebuilt in place against approved design refs;
 * every handler, API call, auth path and tier rule preserved):
 * - Platform-scoped dark palette: --pm-* tokens + @theme inline registrations
 *   bridged under [data-surface='radar-dashboard'], so no global marketing
 *   token was repointed. Light variant derived from the existing palette.
 * - Shell extracted: DashboardShell / DashboardSidebar / DashboardTopBar /
 *   nav-model; page.tsx 4,456 -> ~3,700 lines
 * - New components: ScoreHero, GradeScale, StatStrip, CategoryGrid,
 *   IssuesSummaryCard, ToolResultsTable, InsightsTabs, TrendSparkline,
 *   NewAuditView, ui/{InstrumentLabel,GradeChip,MeterRow,Panel,Chip}
 * - Retired: HealthScoreGauge, ScoreRing, IssuesTable, TrendChart
 * - Category summaries isolated in src/lib/radar/categories.ts — presentation
 *   only, excludes withheld/failed tools, shows -- when nothing is scored
 * - New-audit view, Hive promo card, Priority Roadmap, run comparison,
 *   history row and action items rebuilt to their design references
 * - Content column widened to a fluid max-w-[1520px] with 32px gutters
 * - Sidebar foot now carries the colophon; prospect-score meter, "Watching"
 *   summary and the Chrome-extension link retired with their plumbing
 * - Account page audits deep-link via /platform/app?run=<id> (they were
 *   plain text before, so a saved report could not be reopened)
 * - Trend history fixed: GET and POST were calling an authenticated route
 *   with bare fetch, and POST wrote a null score into a NOT NULL column
 * - resolveAuth gained a development-only identity so localhost exercises
 *   the authenticated paths; inert in production
 *
 * Email + nurture integrity (#106, #113-124):
 * - Compliant Radar free-lead nurture activated, then hardened: anti-stacking
 *   scoped to campaign sends, chat-lead nurture retired, lead confirmations
 *   gated on suppression, mid-nurture leads excluded from campaigns
 * - Newsletter subscriber cohort + directory, welcome template redesign
 * - Admin nurture health dashboard; /admin/broadcasts repointed to real
 *   campaign history; composer retired
 * - Migrations 134-137 (subscriber view, campaign-scoped anti-stacking,
 *   campaign history view + event_timestamp)
 *
 * LLM hub + telemetry (#72-75):
 * - All provider access consolidated onto src/lib/llm-providers.ts, enforced
 *   by ESLint; 23 stray call sites removed
 * - Model registry + per-call cost telemetry (migration 127 llm_calls)
 * - Admin LLM costs dashboard reading the ledger
 *
 * Scoring correctness + drift detection (#86-91):
 * - Withheld tools excluded from the unified score; soft-404 inflation fixed;
 *   readiness treats an absent file as measured
 * - Priority roadmap + score parity gate
 * - Eval-lite: weekly golden-set re-scoring through the real scorer
 *   (migration 133 eval_runs) + read-only admin visibility
 *
 * Brand (#85, #110-112):
 * - Radar identity + grade-chip contrast, unified product-suite mark,
 *   ask identity icon, brand system guidelines page
 *
 * SEO / content (#64-65, #79-83, #92, #99):
 * - Production URLs canonicalized to www, including share URLs and email links
 * - Site-wide noindex mechanism removed; AEO score and self-audit pillars
 * - Sample report page + site-wide links
 * - Blog Article JSON-LD drift rule (carry only @context/@type/image),
 *   enforced by a pre-commit validator
 *
 * Performance, security, infra:
 * - Homepage bundle/LCP work (#70), covering indexes for 19 FKs (#76,
 *   migration 132), RLS policy consolidation (migrations 128-131)
 * - next.js 16.2.11, sharp and next-auth/postcss advisories (#95, #97, #104)
 * - CI + contribution workflow (#63), pinned tsx validation runtime (#66)
 * - UTM attribution events + consent expiry (migration 126)
 *
 * v1.27.0 (Decision-Stage Positioning) - 2026-06-25
 * Homepage:
 * - Replaced the owned-vs-rented WhyOwned section (Section 2) with a
 *   decision-stage AI visibility block: gap table (answer-layer tools vs Radar),
 *   AI decision win-rate north-star line, "a score you can defend", and
 *   operator persona tiles (Growth & SEO / Agencies / Founders & PMs)
 * - Multicolor section headline matching the hero subtitle pattern
 * - WhyOwned.tsx parked (no longer imported)
 *
 * AI-discoverability sweep (decision-stage frame, honesty guardrails held):
 * - knowledge-graph.ts: radar entity description reframed to decision-stage,
 *   alternateName + 6 keywords added; ai-visibility-tools parent reframed
 * - llms.txt + llms-full.txt: Radar reframed as "decision-stage AI visibility"
 * - win-rate kept as keyword/topic only, never asserted as a measured feature;
 *   complement-not-competitor + software-not-hardware disambiguation untouched
 * - sitemap + blog untouched (no new public URL)
 *
 * v1.26.0 (Directory IA + Dashboard Chrome) - 2026-05-29
 * /tools page re-architect:
 * - Featured strip: 3 most-used tools (Crawl Checker, llms.txt Validator, AI Readiness Score)
 *   surfaced from 60-day tool_runs usage data (top 4 = ~85% of runs)
 * - Compact directory grid: remaining 10 tools at 2/3/4-col with category-icon + 2-line desc,
 *   no cover image, whole card is the link
 * - Category chip filter (client-side): All / Page Audit / Site Audit / AI Citations /
 *   Brand Monitoring / Compare & Share — role="tablist" + aria-selected + focus rings
 * - Dropped redundant navy Radar upsell bar above the tool grid (premature interruption)
 * - Bottom CTA repurposed: single earned Radar pitch in brand-cyan gradient
 *   (replaces the prior pink Strategy Session card — wrong offer for self-serve tools audience)
 * - Width consistency: max-w-6xl across hero / Radar bar / featured / directory / bottom CTA
 * - New: src/app/tools/ToolsDirectory.tsx client component
 * - Scroll: ~5000px desktop → ~2000px; ~7800px mobile → ~3500px
 *
 * /platform/app dashboard chrome re-architect:
 * - Responsibility split: top navbar = global context (brand + domain + status),
 *   sidebar = navigation only
 * - Removed redundant sidebar domain header (Globe + activeDomain duplicated the top nav)
 * - Sidebar collapse toggle moved from sidebar to top navbar's left cluster
 *   (Slack/Notion/Linear pattern — always visible, never shifts position)
 * - Sidebar nav now starts at the first row — no header overhead, no orphaned chrome
 *
 * v1.25.0 (Programmatic GEO + AutoSchema Engine) - 2026-05-28
 * AutoSchema engine:
 * - Single-source brand-truth in src/lib/seo/brand-constants.ts
 * - Discriminated-union entity-mode + page-mode covers topics, all services, about, all legal pages,
 *   index/collection pages, search, platform pages (19 pages migrated in one batch)
 * - Pre-deploy gate: npm run validate:schema (45/45 entities pass)
 * - Server+client split pattern for 9 client-only pages
 *
 * Programmatic /answers infrastructure:
 * - 15 published QAPage pages live (5 archetypes x 3)
 * - validate-answers.ts + npm script (15/15 pass); pre-commit gate via husky
 * - /topics surfacing + 14 inbound links from 7 pillar posts (zero orphans)
 * - Migration 091: match_ask_embeddings answer branch
 * - /api/ask returns relatedAnswers; Pixelmojo Agent inline-links matching answers (top 3, sim ≥ 0.5)
 * - Embeddings: 152 total. Smoke-tested: "what is llms.txt" returns top match at 0.62
 *
 * Site freshness pillar:
 * - Google Information Agents freshness blog pillar
 * - Site Freshness Auditor tool with bidirectional citation chain
 * - Dogfood-driven fixes: BlogHero dateTime, auditor feed regex order, @graph parsing,
 *   blog path coverage, sitemap-index cap
 *
 * Code Ownership pillar:
 * - New pillar post + knowledge-graph entity
 *
 * Tools + UX:
 * - In-flow Pro Retainer upsell on tools
 * - OG Auditor tool + /tools page B layout
 * - Virality challenge: embeddable Radar badge
 * - Radar tool detail parity (paid matches free /tools/* per-tool detail)
 * - Header transparent bg on load, snaps to solid on scroll
 * - Footer rebuild: Resend + Paddle added, Cursor swapped for Langfuse,
 *   Anthropic swapped for Claude Code, logo sizing system, unified hover, reordered by function
 *
 * Skills (radar discipline):
 * - radar-tool-add (end-to-end tool scaffolding orchestrator)
 * - radar-identity-audit (4 auth paths coherence check)
 * - radar-fix (root-cause discipline gate)
 *
 * Infrastructure:
 * - radar-feedback cron killed (vercel.json entry removed + route stubbed)
 * - charge refactor
 * - Supabase MCP server config + migration grant pre-commit check
 * - Outreach: 6 free-tool user + 6 mid-tier prospect templates; 12 mails sent 2026-05-28
 *
 * v1.24.0 (Owned Telemetry + AI Visibility Hardening) - 2026-05-16
 * Owned telemetry:
 * - Dropped MLflow / Railway dependency entirely; new public.tool_runs table is source of truth
 * - ToolTelemetry helper replaces mlflow client; same API, single insert per run
 * - Migrations 081 + 082: tool_runs schema + full pivot of historical MLflow data
 * - Migration 083: cost/token column key fallback (cost_usd OR total_cost_usd, etc)
 * - Migration 084: get_tool_activity_stats RPC for accurate /admin/tool-activity stats
 *   (was reading from page-limited rowset, showed "Runs: 200" forever)
 *
 * Hallucination test upgrade:
 * - Multi-page ground truth: sitemap-driven + fallback to /about, /pricing, /team, /products
 * - Ground truth window 4k → 16k chars, per-page cap 6k
 * - Dropped 1500-char truncation on LLM responses (late hallucinations now visible)
 * - LLMs asked to list source URLs; cited URLs threaded into judge; flag.sourceUrl
 * - GroundTruth carries pagesIngested + charsIngested for telemetry
 * - Prompt version 1.0.0 → 1.1.0
 *
 * Radar Brand Index module (shipped 2026-05-09):
 * - 50 curated brands tracked at /labs/brand-index with weekly cron
 * - Methodology page, JSON-LD, blog post (50-brands-audited-half-invisible-to-ai-search)
 * - Brand-index cron tuned 2026-05-16: 8 → 2 brands/day (~3.5 weeks per brand, 4x cost drop)
 *
 * Admin dashboards:
 * - /admin/tool-activity: real-data activity feed (runs, cost, domains, fingerprints, failure rate)
 * - /admin/lead-signals: hot free-tier domains → Research → Perplexity enrich → outreach_prospects
 * - /admin/access-anomalies: detection + auto-resolve modal (mig 080)
 *
 * Radar Self-Audit (false positives fixed):
 * - AEO auditor: speakable now accepted on any CreativeWork (was Article-only)
 * - schema-checker: stopped requiring Article on every page (was page-type-blind)
 * - Bot-name interpolation in audit recs (was hardcoded GPTBot/Claude-Web templates)
 *
 * Security audit (commits 1-3):
 * - JsonLd primitive replaces 48 dangerouslySetInnerHTML instances
 * - ESLint react/no-danger:error rule + CLAUDE.md hard rule
 * - Iframe sandbox for broadcasts-admin HTML preview
 * - Webhook auth/ownership/SSRF, share-ID entropy upgrade, audit-runs stats leak fix
 * - Trend-data session-or-token auth gate (resolveAuth helper)
 * - Audience filter migration: user_credits as canonical paid ledger
 *
 * AI policy + brand entity:
 * - Selective bot allow-list: CCBot, Google-Extended, anthropic-ai, Applebot-Extended now allowed
 * - Disambiguation across structured-data, llms.txt, ask-corpus (Pixel Mojo Productions + pixel art editor confusions)
 * - KNOWN_DOMAIN_BRANDS map in detect-brand bypasses GPT-4o-mini hallucinations
 *
 * Homepage / blog SEO:
 * - WebPage + FAQPage + ItemList JSON-LD with speakable spec
 * - Pillar-post surfacing (lead pillar + latest non-pillar mix)
 * - Exact-phrase FAQ for "What is Pixelmojo and what do they specialize in?"
 * - Speakable schema on Article JSON-LD (blog posts)
 *
 * YouTube Brand Monitor Layer 2:
 * - Transcript analysis matching the gemini-25-youtube-ai-readable article
 *
 * Score Trend bug fix:
 * - snake_case → camelCase mapping in /api/platform/trend-data
 *
 * v1.21.0 (Full-Site Messaging Overhaul) - 2026-04-10
 * - Homepage restructured: 12 sections to 7 with problem-first narrative arc
 * - Hero: "Score leads. Run agents. Audit visibility." (outcome-first, not product names)
 * - Why Owned moved from position 8 to position 2 (strongest differentiator)
 * - ProductComparison: Radar replaces Custom Build as third product card
 * - Nav: Services removed from top-level, Projects/Blog as direct links
 * - Footer: "AI products you own. Intelligence we maintain." + Radar CTA
 * - Pricing: 4 routing cards (Products/Radar/Growth/Custom), cross-model FAQ
 * - Services consolidated 7 to 4: AI Visibility, AI Product Dev, Brand & Sales Design, Growth Marketing
 * - 3 deprecated service pages deleted with permanent redirects
 * - All 4 service pages rewritten: honest voice, ICPs, disqualifiers, plain English
 * - ServicesGrid, ServiceNavigation, ServicesPreview updated to 4 services
 * - Hive pricing corrected $1 to $2/resolution across 20+ files
 * - Vector pricing corrected 3% to 5% on pricing page
 * - Knowledge graph: 3 deleted entities removed, 2 renamed, relatedEntities cleaned
 * - Structured data: knowsAbout array rebuilt for AI product studio identity
 * - llms.txt: services rewritten as 4-service structure, capabilities updated
 * - llms-full.txt: deprecated services removed, consolidated entries added
 * - Chat agent system prompt: 6 services to 4, email templates redirected
 * - Outreach, lead emails, recap generator, session memory all aligned
 * - Scroll animations: LenisReveal rewritten with IntersectionObserver (was rendering immediately)
 * - View transitions: CSS crossfade for page navigation
 * - All meta titles under 60 chars, descriptions under 160 chars
 * - Sitemap dates updated, deprecated URLs removed
 *
 * v1.20.0 (Hallucination-First Messaging + Origin Story) - 2026-04-06
 * - Platform hero rewritten: "Is AI lying about your brand?" hallucination-first messaging
 * - Pricing page aligned to hallucination-first copy
 * - RadarCTA updated globally: "Is AI making things up about your brand?"
 * - Origin story proof sections wired into /platform and /platform/pricing
 * - Blog: Part 2 of AI Visibility Stack series (origin story with git commit evidence)
 * - OriginStoryTimeline and LLMAnswerDiffDiagram blog components
 * - Slim action plan bar replacing 3-card hero in radar dashboard
 * - Flex alignment fix: FeatureCard component + global CSS rule for p/h in flex containers
 * - h3/h4 to span conversion across 8 tool pages and 9 blog diagram files
 * - Security: XSS prevention, Snyk taint flow break in advisor href
 *
 * v1.19.0 (Ahrefs-Style Dashboard Rebuild) - 2026-04-04
 * - Complete dashboard rebuild: Ahrefs-style card grid (donut charts, health gauge, issues bars)
 * - HealthScoreGauge: speedometer-style semi-circular gauge replacing ScoreRing
 * - DashboardOverview: 3-row card grid with tool score distribution, health score, issues distribution
 * - IssuesTable: sortable table with Cross-Tool Insights and Top Issues tabs
 * - ScoreInterpretation: business context panel translating scores into impact (loss aversion framing)
 * - Full light/dark theme support: all components use Tailwind theme classes (text-foreground, bg-card, etc.)
 * - AnimatedThemeToggle added to platform nav (same View Transitions API as main site)
 * - Removed 144+ hardcoded dark-mode hex colors across 9 component files
 * - SVG elements use CSS variables (fill, stroke) for proper theme switching
 * - Warning text contrast fix: --warning-text CSS variable (amber #d97706 light, yellow #FDC304 dark)
 * - Sidebar re-architecture: sole navigation (removed duplicate inline tab strip)
 * - Sidebar groups: Overview, Tools (with scores), Reports with Ahrefs-style left accent bar
 * - Page layout re-architecture: each tab owns full content area, no stacking
 * - Input form collapses to compact summary bar after scan (domain + score + New Test)
 * - Auto-scroll to top on tab switch
 * - CTA hierarchy: magenta primary (Run Audit, Generate AI Prompt, Access), blue secondary, ghost tertiary
 * - Service bridge CTAs: "Need help?" upsell after every 3rd action item
 * - SmartCTA moved above issues table for earlier visibility
 * - Live scan progress: button shows "Scanning 4/12 tools", nav badge shows tool count, top progress bar
 * - Font sizes bumped 1-2px across all components to match Ahrefs density
 * - RadarDashboardPreview rebuilt: visual replica of new dashboard for home and /platform pages
 * - Light theme set as default for platform
 * - Locale selector redesigned: full-height input with inline globe icon
 * - AdvisorChat, SmartCTA, ToolDetailPanel, ActionsPanel, ThreadsPanel, TrendChart all theme-converted
 *
 * v1.18.0 (TBE DIY Implementation) - 2026-04-03
 * - AI prompt generator: every action item produces context-rich prompts pre-filled with audit data
 * - 5 implementation threads: Crawlability, Structured Data, LLM Communication, Content Authority, Citation Building
 * - Thread-aware prompts: each step includes context from previous steps
 * - llms.txt starter generator: produces starter file from audit data
 * - JSON-LD schema markup generator: copy-pasteable blocks for missing schema types
 * - Single-tool re-verify: re-run any completed tool without full re-audit
 * - Persistent thread progress tracking (localStorage per domain)
 * - Threads tab: 6th dashboard tab with sidebar navigation and keyboard shortcut (6)
 * - Version display fix: changed from #1e293b to #475569 for dark mode visibility
 * - Updated messaging across homepage, /platform, knowledge graph, llms.txt, RadarCTA, 3 blog posts
 *
 * v1.17.0 (AI Advisor Overhaul) - 2026-04-02
 * - Radar AI Advisor: multi-turn conversations with full history
 * - 9 GEO/AEO/LLM methodology training (Bailyn, Barnard, Gubur, King, Fishkin, Indig, Solis, Ray, Huang)
 * - Rich audit context: advisor sees actual findings (blocked bots, missing schema, sentiment) not just scores
 * - Dynamic suggested questions generated from audit results
 * - 3-tier engagement escalation: free flow (1-5), soft CTA (6-10), engagement gate (11+)
 * - Layered system prompt builder following Vector chatbot architecture
 * - Audit context builder extracts relevant findings from all 12 tools
 * - Input sanitization on auditContext and conversation history (security hardening)
 * - max_tokens increased from 500 to 800 for substantive consultative responses
 * - Mobile responsive advisor chat (full-width on mobile, 384px on desktop)
 * - Platform footer: version display
 *
 * v1.16.1 (GSC Admin & Security Hardening) - 2026-03-31
 * - GSC admin route rebuilt from scratch with proper error handling
 * - Valid search appearances discovered dynamically from GSC API
 * - Radar leads page rebuilt with no navigation sinks (XSS prevention)
 * - Dynamic hrefs removed from radar leads page (security hardening)
 * - Nav: LocalTime hidden below 15" monitors (2xl breakpoint)
 *
 * v1.16.0 (Radar v2 & SSRF Hardening) - 2026-03-30
 * - Radar v2: 12-tool AI visibility platform (up from 8)
 * - 4 new tools: hallucination detection, prompt SOV, schema audit, source influence
 * - Radar engine: 13 lib modules (actions, insights, scoring, tool-registry, etc.)
 * - Radar UI: 7 components (ScoreRing, ToolResultCard, TrendChart, ActionsPanel, etc.)
 * - Radar history: load saved runs back into dashboard
 * - SSRF hardening: shared fetchExternal() in sanitize.ts replaces 7 duplicated fetch functions
 * - validateExternalUrl: blocks private IPs, localhost, cloud metadata (169.254), non-http schemes
 * - XSS: added data: and vbscript: scheme blocking in extractInternalLinks
 * - Token usage: TOKEN_LIMIT reduced to 1 (1 free use per tool per user)
 * - Homepage: Radar as third product pillar ("Vector qualifies. Hive orchestrates. Radar reveals.")
 * - Product metadata: competitive category language (HubSpot/Intercom/Semrush alternative)
 * - Entity disambiguation: disambiguatingDescription, legalName, naics, llms.txt contrast table
 * - AEO: WebPage + FAQPage schema on homepage, SpeakableSpecification, Last-Modified header
 * - llms.txt: Radar Platform section, disambiguation section, updated URLs
 * - Blog: Radar v2 announcement post with RadarV2Diagrams (7 components)
 * - Deleted: Brand Gap book review post (copyright risk)
 * - 3 new DB migrations (043-045): trend data, audit runs, webhook configs
 * - next.config: images.qualities [75, 95] for Next.js 15 compatibility
 * - Platform APIs: audit-runs, trend-data, webhooks
 *
 * v1.15.0 (GEO/AEO & Next.js 16 Compatibility) - 2026-03-27
 * - Next.js 16 + React 19 compatibility: cookie-based theme SSR (ThemeProvider), CSP unsafe-eval dev-only
 * - Speakable schema on all blog Article JSON-LD (headline, description, key takeaways)
 * - Organization + Person knowsAbout linked to knowledge graph entities via @id
 * - StatBlock component for AEO-formatted data callouts
 * - KeyTakeaways alias for TLDR with data-key-takeaways speakable target
 * - 21 posts retrofitted with StatBlocks (24 total) for LLM citation optimization
 * - BlogHero data attributes for speakable targeting
 * - AEO content standards added to CLAUDE.md and blog skill
 *
 * v1.12.2 (LLM Visibility & Chat UX) - 2026-02-04
 * - LLM visibility optimization: author attribution 100% coverage (31 posts)
 * - Added wordCount to all blog frontmatter for content length signals
 * - AggregateRating schema added to 6 products/services
 * - NewsArticle schema for AI Technical Debt series (3 posts)
 * - Series field added for cross-linking related posts
 * - Chat typewriter effect for AI responses (smooth character reveal)
 * - Auto-scroll during typewriter animation
 *
 * v1.12.1 (Content & Quality Improvements) - 2026-02-01
 * - AI technical debt blog series: 3 posts with full cross-linking
 * - JSON-LD domain consistency fixes (pixelmojo.io, /blogs/ path)
 * - llms.txt updated with featured blog content
 * - Test coverage: 0% → 80 tests (lead-qualification, spam-detection, sanitize, rate-limit)
 * - Chat route refactored: 3,289 → 1,060 lines (extracted 3 modules)
 * - Vitest testing framework configured with coverage reporting
 *
 * v1.12.0 (Thread Engineering & UI Refresh) - 2026-01-24
 * - New blog: Thread-Based Engineering framework with 10 visual diagrams
 * - Thread diagrams: Base, P-Threads, L-Threads, C-Threads, F-Threads, B-Threads, Z-Threads
 * - Added Core Four, Four Dimensions, and Implementation Path diagrams
 * - Featured Projects: Horizontal carousel with navigation arrows
 * - Compact project cards with snap scrolling and "View All" end card
 * - Centered header layout for Featured Projects section
 *
 * v1.11.0 (Ownership & Performance Model) - 2026-01-05
 * - New tagline: "Built for you. Powered by us." as H2 subtitle
 * - Clarified ownership model: application code vs. intelligence platform
 * - Token costs included in performance share (no client API keys needed)
 * - Fair use limits: Vector 500 conv/mo, Hive 2,000 res/mo
 * - 90-day mutual performance review for accountability
 * - Updated FAQs with high-volume and performance questions
 * - Consistent messaging across 27 files
 * - Protected IP by removing specific tech stack references
 *
 * v1.10.0 (Vector & Hive Products Launch) - 2026-01-01
 * - Vector landing page: 12-dimension qualification system ($25K-$40K)
 * - Hive landing page: Multi-agent orchestration powered by Vector ($50K-$75K)
 * - Enhanced JSON-LD structured data with OfferCatalog and pricing
 * - Comprehensive llms.txt update for LLM discoverability
 * - Chat widget engagement overhaul (v2.14.0):
 *   - Visible "Ask Vector" label on chat button
 *   - Larger button/icon, bouncy badge animation
 *   - 6 quick replies (3 exploratory + 3 transactional)
 *   - Proactive messaging for return visitors
 *   - Faster trigger timing (8-20s based on intent)
 * - Homepage refresh with Vector/Hive product positioning
 * - Footer updated for 2026 launch
 *
 * v1.9.0 (Previous version - see git history)
 *
 * v1.8.0 (AI Positioning & Capabilities) - 2025-11-08
 * - Version now auto-syncs with package.json (no more manual updates!)
 * - New /capabilities page with 10 comprehensive FAQs
 * - Enhanced structured data (FAQPage schema addressing AI misrepresentation)
 * - Updated llms.txt and ai-capabilities-factsheet.txt with positioning
 * - Geography targeting in meta descriptions (Manila focus)
 * - Navigation updates (Services dropdown + footer)
 * - Fixed branding consistency (PixelMojo → Pixelmojo)
 * - Updated the primary logo asset path
 * - Removed social sharing buttons from BlogHero
 * - Added /capabilities to sitemap
 * - React 19.1.0 → 19.2.0 upgrade
 *
 * v1.7.0 (Enhanced Search & Navigation) - 2025-11-04
 * - Complete search page redesign with advanced filtering and sorting
 * - Category filtering (All, Blog, Project, Service) with result counts
 * - Sort options: Relevance, Title (A-Z), Category
 * - Search term highlighting in results (titles and descriptions)
 * - Pagination (10 results per page) with Previous/Next controls
 * - Recent searches functionality (last 5 searches saved in localStorage)
 * - URL state management (query params sync for sharing/bookmarking)
 * - Category breakdown display (e.g., "5 blogs, 3 projects")
 * - Clickable category badges to filter by category
 * - Enhanced empty state with quick search suggestions
 * - Keyboard shortcuts (⌘K/Ctrl+K to focus search)
 * - Improved dropdown menus (Services, Projects, Blogs, Search)
 * - Full keyboard navigation support (Arrow keys, Enter, Escape)
 * - Click-outside-to-close functionality
 * - Better visual hierarchy and spacing
 * - Accessibility improvements (ARIA attributes, focus management)
 * - Code standards compliance (removed console.error, using cn() utility)
 * - SEO improvements (noindex metadata for search pages)
 *
 * v1.6.0 (Previous version - see git history)
 *
 * v1.5.0 (Intelligence & Analytics) - 2025-10-10
 * - Email notification system for qualified leads (Resend integration)
 * - Analytics dashboard with conversion funnel visualization
 * - Unified admin interface with tab navigation
 * - Server-side A/B testing framework (Vercel Edge Config)
 * - Advanced lead scoring (0-100 scale with weighted criteria)
 * - Zod validation for API endpoints
 * - Rate limiting on admin endpoints (10 req/min)
 * - Memoized components for performance optimization
 * - Route-level loading and error states
 * - Production-ready code quality improvements
 *
 * v1.4.0 (Progressive Engagement) - 2025-10-09
 * - Progressive disclosure UX for chat widget
 * - Context-aware chat triggering based on page type
 * - Geolocation-based widget visibility (PH-only)
 * - Markdown rendering in chatbot (headings, bullets)
 * - Security improvements (rate limiting, error handling)
 * - Loading/error states for all service pages
 * - Removed placeholder project examples
 *
 * v1.3.0 (Conversions) - 2025-10-06
 * - AI-powered chatbot with OpenAI integration
 * - Admin dashboard for chat conversations
 * - Lead qualification and tracking system
 * - Exit intent and proactive engagement triggers
 * - Session persistence with localStorage
 * - Rate limiting and message count tracking
 *
 * v1.2.0 (Search) - 2025-01-04
 * - Added client-side search with Fuse.js
 * - Search modal with keyboard shortcuts (⌘K)
 * - Index blogs, projects, services, and pages
 * - Fuzzy search with 20 result limit
 *
 * v1.1.0 (Monitoring) - 2025-10-03
 * - Integrated Sentry error monitoring
 * - Enhanced error tracking and performance monitoring
 *
 * v1.0.0 (Launch) - 2025
 * - Initial website launch
 * - Blog system with Contentlayer
 * - Project portfolio
 * - Service pages
 */
